import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiLoader, FiCheck } from "react-icons/fi";
import Card from "../../../../../../components/ui/Card";
import { DietaryPreference } from "../../../../../../types/dietary";
import { getItemDietaryPreferences } from "../../../../../../services/dietaryService";
import { getDisplayDietaryName, getDisplayDietaryDescription } from "../../../../../../utils/dietaryHelper";

interface DietaryPreferencesSectionProps {
  itemId: string;
  cardVariants?: any;
}

const DietaryPreferencesSection: React.FC<DietaryPreferencesSectionProps> = ({
  itemId,
  cardVariants,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [dietaryPreferences, setDietaryPreferences] = useState<
    DietaryPreference[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDietaryPreferences = async () => {
      try {
        setIsLoading(true);
        const preferences = await getItemDietaryPreferences(itemId);
        setDietaryPreferences(preferences);
        setError(null);
      } catch (err) {
        console.error("Error loading item dietary preferences:", err);
        setError(
          t("menuItems.errors.dietaryPreferencesFetchFailed") ||
          "Failed to load dietary preferences"
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (itemId) {
      fetchDietaryPreferences();
    }
  }, [itemId, t]);

  if (isLoading) {
    return (
      <Card className="p-4 sm:p-6 border-none shadow-md">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-lime-100 dark:bg-lime-900/30 rounded-lg flex items-center justify-center">
            <FiCheck className="w-4 h-4 sm:w-5 sm:h-5 text-lime-600 dark:text-lime-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("menuItems.dietaryPreferences")}
          </h3>
        </div>

        <div className="py-12 text-center">
          <div className="inline-flex items-center justify-center">
            <FiLoader className="w-8 h-8 sm:w-10 sm:h-10 text-lime-600 dark:text-lime-400 animate-spin" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base font-medium mt-4">
            {t("common.loading")}
          </p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-4 sm:p-6 border-none shadow-md">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-lime-100 dark:bg-lime-900/30 rounded-lg flex items-center justify-center">
            <FiCheck className="w-4 h-4 sm:w-5 sm:h-5 text-lime-600 dark:text-lime-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("menuItems.dietaryPreferences")}
          </h3>
        </div>

        <div className="py-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-red-100 dark:bg-red-900/20 rounded-full mb-4">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-red-600 dark:text-red-400 text-sm sm:text-base font-medium">
            {error}
          </p>
        </div>
      </Card>
    );
  }

  if (dietaryPreferences.length === 0) {
    return (
      <Card className="p-4 sm:p-6 border-none shadow-md">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-lime-100 dark:bg-lime-900/30 rounded-lg flex items-center justify-center">
            <FiCheck className="w-4 h-4 sm:w-5 sm:h-5 text-lime-600 dark:text-lime-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("menuItems.dietaryPreferences")}
          </h3>
        </div>

      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6 border-none shadow-md">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-lime-100 dark:bg-lime-900/30 rounded-lg flex items-center justify-center">
          <FiCheck className="w-4 h-4 sm:w-5 sm:h-5 text-lime-600 dark:text-lime-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("menuItems.dietaryPreferences")} ({dietaryPreferences.length})
        </h3>
      </div>

      {/* Dietary Preferences Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {dietaryPreferences.map((preference) => (
          <div
            key={preference.id}
            className={`bg-gradient-to-br from-lime-50 to-emerald-50 dark:from-lime-900/20 dark:to-emerald-900/20 border border-lime-200 dark:border-lime-800 rounded-lg p-4 hover:shadow-md transition-all ${isRTL ? 'text-right' : 'text-left'}`}
            style={{ direction: isRTL ? 'rtl' : 'ltr' }}
          >
            {/* Name */}
            <h4 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base mb-1">
              {getDisplayDietaryName(preference, i18n.language)}
            </h4>

            {/* Description */}
            {getDisplayDietaryDescription(preference, i18n.language) && (
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                {getDisplayDietaryDescription(preference, i18n.language)}
              </p>
            )}
          </div>
        ))}
      </div>

    </Card>
  );
};

export default DietaryPreferencesSection;
